import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Tv, Lock, Radio as RadioIcon, Loader2, Play, RefreshCw } from "lucide-react";
import { LoadError } from "@/components/LoadError";
import { toast } from "sonner";
import { endpoints, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MediaPlayer } from "@/components/MediaPlayer";
import { UnlockButton } from "@/components/UnlockButton";
import { LiveChat } from "@/components/LiveChat";
import { MembersBadge } from "@/components/MembersBadge";
import { AdSlot } from "@/components/AdSlot";
import { usePreviewGate, PreviewPill } from "@/components/PreviewGate";

const OfflineScreen = ({ schedule, overlay = false, onRetry }) => (
  <div data-testid="livetv-offline"
    className={overlay
      ? "absolute inset-0 z-10 bg-[#0A0C10]/95 backdrop-blur-sm flex flex-col items-center justify-center text-center p-6 overflow-auto"
      : "rounded-2xl border border-[#273042] bg-[#121620] p-8 sm:p-12 text-center mb-8"}>
    <img src="/bb-logo.png" alt="B&B KIGALI" className="h-20 sm:h-24 w-auto mx-auto mb-5 rounded-lg" onError={(e) => { e.currentTarget.style.display = "none"; }} />
    <div className="text-white font-bold text-xl">We'll be back soon. Stay with B&B KIGALI.</div>
    <p className="text-slate-300 text-sm mt-2 max-w-lg mx-auto">Your live program will resume automatically. Meanwhile, here's what's coming up.</p>
    <div className="mt-5 flex flex-wrap justify-center gap-3">
      {onRetry && (
        <button onClick={onRetry} data-testid="livetv-retry-btn"
          className="inline-flex h-11 px-6 rounded-full bg-white/10 border border-white/20 text-white font-bold items-center gap-1.5 hover:bg-white/15 transition-colors">
          <RefreshCw className="w-4 h-4" aria-hidden="true" /> Retry stream
        </button>
      )}
      <Link to="/radio" className="inline-flex h-11 px-6 rounded-full bg-[#E11D2A] text-white font-bold items-center gap-1.5"><RadioIcon className="w-4 h-4" aria-hidden="true" /> Listen to radio</Link>
    </div>
    {schedule.length > 0 && (
      <div className="mt-8 text-left w-full max-w-2xl mx-auto" data-testid="livetv-upcoming">
        <h3 className="text-white font-bold mb-3 flex items-center gap-2"><Tv className="w-5 h-5 text-[#E11D2A]" /> Upcoming programs</h3>
        <div className="rounded-xl overflow-hidden border border-[#273042] divide-y divide-[#273042]">
          {schedule.slice(0, 8).map((s) => (
            <div key={s.id} data-testid={`upcoming-${s.id}`} className="flex items-center gap-3 p-3 bg-[#0A0C10]">
              <div className="font-mono text-xs text-slate-300 w-28 shrink-0">{s.time}</div>
              <div className="min-w-0">
                <div className="text-white text-sm font-semibold truncate">{s.showTitle}</div>
                {s.djName && <div className="text-xs text-slate-400 truncate">{s.djName}</div>}
              </div>
              {s.status === "on-air" && <span className="ml-auto text-[10px] font-bold text-[#EF4444] flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] live-dot" /> ON AIR</span>}
            </div>
          ))}
        </div>
        <Link to="/schedule" className="mt-3 inline-block text-sm text-[#E11D2A] font-semibold">View full schedule →</Link>
      </div>
    )}
  </div>
);

export default function LiveTV() {
  const { isSubscribed, isAuthed } = useAuth();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [source, setSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [schedule, setSchedule] = useState([]);
  const [unavailable, setUnavailable] = useState(false);
  const [failed, setFailed] = useState(false);
  const [playerKey, setPlayerKey] = useState(0);
  const subRef = useRef(isSubscribed);
  subRef.current = isSubscribed;
  const itemsSigRef = useRef("");
  const schedSigRef = useRef("");

  const load = async (silent = false) => {
    try {
      let streamsFailed = false;
      const [s, c] = await Promise.all([
        endpoints.liveStreams().catch(() => { streamsFailed = true; return { data: { streams: [] } }; }),
        endpoints.liveChannels().catch(() => ({ data: { channels: [] } })),
      ]);
      const anyLiveChannel = (c.data.channels || []).some((x) => x.isLive);
      // Couldn't reach the server for live streams and nothing else is live: show a clear error + retry.
      if (streamsFailed && !anyLiveChannel) { if (!silent) setFailed(true); return; }
      setFailed(false);
      endpoints.schedule().then((r) => {
        const sched = r.data || [];
        const sig = JSON.stringify(sched.map((x) => [x.id, x.status]));
        if (sig !== schedSigRef.current) { schedSigRef.current = sig; setSchedule(sched); }
      }).catch(() => {});
      const streamItems = (s.data.streams || []).map((x) => ({ ...x, kind: "stream" }));
      const chanItems = (c.data.channels || []).filter((c2) => c2.isLive).map((c2) => ({
        kind: "channel", id: c2.key, title: c2.title || c2.channelName, channelName: c2.channelName,
        sourceType: "youtube", isPremium: false, isLive: true, thumbnail: c2.thumbnail,
        source: c2.videoId ? { type: "youtube", youtubeId: c2.videoId } : null,
        locked: !!c2.locked || !subRef.current,
      }));
      const all = [...streamItems, ...chanItems];
      // Only push new item state when the meaningful shape changed — prevents a 20s grid flash.
      const sig = JSON.stringify(all.map((x) => [x.kind, x.id, x.isLive, x.locked, x.thumbnail]));
      const changed = sig !== itemsSigRef.current;
      if (changed) { itemsSigRef.current = sig; setItems(all); }
      const first = all.find((x) => x.isLive) || null;
      // Keep the currently-active item stable during polling to avoid remounting the player.
      setActive((prev) => (silent && prev && first && prev.id === first.id ? prev : first));
      // Non-subscribers start their free preview only when they press play.
      if (!silent) {
        if (first && !first.locked && first.source && subRef.current) setSource(first.source);
      } else if (!first) {
        // Broadcast genuinely gone → clear the player and show the offline screen.
        setSource(null); setUnavailable(false);
      } else if (first && first.isLive && !first.locked && first.source && subRef.current) {
        setSource((cur) => cur || first.source); // restore source if it had been cleared
      }
    } catch (e) { if (!silent) toast.error(apiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    // Re-runs when sign-in/subscription state resolves so a member is never stuck on a locked view.
    itemsSigRef.current = "";
    load();
    const t = setInterval(() => load(true), 20000); // auto-recover / auto-detect end without refresh
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [isSubscribed, isAuthed]);

  const [previewEnded, setPreviewEnded] = useState(false);
  const preview = usePreviewGate("tv", !!source && !isSubscribed, () => { setSource(null); setPreviewEnded(true); toast.error("Your free preview has ended — subscribe to keep watching"); });
  useEffect(() => {
    if (isSubscribed) { setPreviewEnded(false); return; }
    endpoints.previewState("tv").then(({ data }) => setPreviewEnded(data.phase === "expired")).catch(() => {});
  }, [isSubscribed]);
  const grace = preview?.phase === "grace" && !isSubscribed && !!source;

  const retryPlayer = () => { setUnavailable(false); setPlayerKey((k) => k + 1); };

  const select = async (it) => {
    setActive(it); setSource(null); setUnavailable(false);
    if (!it.isLive) return;
    if ((it.locked || previewEnded) && !isSubscribed) {
      if (!isAuthed) window.dispatchEvent(new Event("open-auth"));
      else window.dispatchEvent(new CustomEvent("open-subscribe"));
      return;
    }
    if (it.source) { setSource(it.source); return; }
    if (it.kind === "stream") {
      setStarting(true);
      try {
        const { data } = await endpoints.liveStreamPlayback(it.id);
        setSource(data.source);
      } catch (e) {
        if (e?.response?.status === 403) toast.error("Subscribe to watch this live stream.");
        else toast.error(apiError(e?.response?.data?.detail));
      } finally { setStarting(false); }
    }
  };

  const locked = active && (active.locked || previewEnded) && !isSubscribed;

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-10">
      <div className="flex items-center gap-2 mb-6">
        <Tv className="w-7 h-7 text-[#E11D2A]" />
        <h1 className="text-3xl sm:text-4xl font-extrabold text-white">Live TV</h1>
      </div>

      <MembersBadge className="mb-6" />

      <AdSlot placement="tv" position="top" className="mb-6" />

      {loading ? (
        <div className="aspect-video rounded-2xl bg-[#121620] animate-pulse mb-8 flex items-center justify-center" data-testid="livetv-loading">
          <Loader2 className="w-8 h-8 text-slate-400 animate-spin" aria-label="Loading Live TV" />
        </div>
      ) : failed ? (
        <div className="mb-8"><LoadError onRetry={() => { setLoading(true); load(); }} testid="livetv-error" message="We couldn't load Live TV right now." /></div>
      ) : active && active.isLive ? (
        <div className="grid lg:grid-cols-3 gap-6 mb-8 items-stretch">
        <div className="lg:col-span-2 rounded-2xl overflow-hidden border border-[#273042] self-start">
          {locked ? (
            <div className="aspect-video bg-[#121620] flex flex-col items-center justify-center text-center p-6" data-testid="livetv-paywall">
              <Lock className="w-10 h-10 text-[#2563EB] mb-3" />
              <div className="text-white font-bold text-lg">{active.title || active.channelName} is live now</div>
              <p className="text-slate-400 text-sm mt-1" data-testid="livetv-paywall-text">{previewEnded ? "Your free preview has ended. Subscribe to keep watching." : "Subscribe to watch this premium live stream."}</p>
              <Link to="/pricing" data-testid="livetv-subscribe-btn" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("open-subscribe")); }} className="mt-5 h-11 px-6 rounded-full bg-[#E11D2A] hover:bg-[#C8121F] text-white font-bold btn-glow flex items-center gap-1.5"><Lock className="w-4 h-4" /> Unlock Live TV</Link>
            </div>
          ) : source ? (
            <div className={grace ? "fixed bottom-3 left-3 z-[110] w-44 sm:w-80 rounded-xl overflow-hidden border border-amber-400/60 shadow-2xl bg-black" : "relative"} data-testid={grace ? "livetv-mini-player" : "livetv-player-wrap"}>
              {!isSubscribed && <div className={`absolute z-20 ${grace ? "top-1.5 left-1.5" : "top-3 left-3"}`}><PreviewPill st={preview} label="Live TV" testid="tv-preview-pill" compact={grace} /></div>}
              <MediaPlayer key={playerKey} source={source} title={active.title} testid="livetv-player"
                onUnavailable={() => setUnavailable(true)} onRecovered={() => setUnavailable(false)} />
              {unavailable && (grace
                ? <div className="absolute inset-0 z-10 bg-black/85 flex items-center justify-center text-xs text-white font-semibold" data-testid="livetv-mini-offline">Reconnecting…</div>
                : <OfflineScreen schedule={schedule} overlay onRetry={retryPlayer} />)}
            </div>
          ) : (
            <div className="aspect-video bg-[#0A0C10] flex flex-col items-center justify-center">
              <button onClick={() => select(active)} disabled={starting} data-testid="livetv-play-btn" aria-label="Play Live TV"
                className="w-16 h-16 rounded-full bg-[#E11D2A] flex items-center justify-center btn-glow disabled:opacity-60">
                {starting ? <Loader2 className="w-7 h-7 text-white animate-spin" /> : <Play className="w-7 h-7 text-white ml-1" />}
              </button>
            </div>
          )}
          <div className="p-4 flex items-center gap-2 bg-[#0A0C10]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] live-dot" />
            <span className="text-white font-semibold">{active.title || active.channelName}</span>
            {active.isPremium && <span className="ml-2 px-2 py-0.5 rounded bg-[#2563EB] text-white text-[10px] font-extrabold">PREMIUM</span>}
          </div>
        </div>
        <LiveChat streamId={active.id} />
        </div>
      ) : (
        <OfflineScreen schedule={schedule} />
      )}

      {items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((c) => (
            <button key={`${c.kind}-${c.id}`} onClick={() => select(c)} data-testid={`livetv-item-${c.id}`}
              className={`text-left rounded-xl overflow-hidden bg-[#121620] border card-hover ${active && active.id === c.id ? "border-[#E11D2A]" : "border-[#273042]"}`}>
              <div className="aspect-video relative overflow-hidden">
                <img src={c.thumbnail || "/bb-logo.png"} alt={c.title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = "/bb-logo.png"; }} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                {c.isLive
                  ? <span className="absolute top-2.5 left-2.5 flex items-center gap-1 px-2 py-1 rounded-md bg-[#EF4444] text-white text-[10px] font-extrabold uppercase"><span className="w-1.5 h-1.5 rounded-full bg-white live-dot" /> Live</span>
                  : <span className="absolute top-2.5 left-2.5 px-2 py-1 rounded-md bg-black/70 text-slate-300 text-[10px] font-bold uppercase">Offline</span>}
                {c.isPremium && <span className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded bg-[#2563EB] text-white text-[10px] font-extrabold">PREMIUM</span>}
                <UnlockButton locked={c.locked} testid={`unlock-live-${c.id}`} />
              </div>
              <div className="p-4">
                <div className="text-white font-semibold">{c.title || c.channelName}</div>
                <div className="text-xs text-slate-400">{c.programName || c.channelName || c.sourceType}</div>
              </div>
            </button>
          ))}
        </div>
      )}
      <AdSlot placement="tv" position="bottom" className="mt-8" />
    </div>
  );
}
